import { CdkScrollable, ScrollDispatcher as CdkScrollDispatcher } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Provider,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { EditorView } from 'prosemirror-view';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';

import { EmojiPickerService } from '@recruitee/emoji';
import {
  EditorJSONState,
  EditorButton,
  EditorMode,
  isEmptyDoc,
  PluginsEvents,
} from '@recruitee/prosemirror';

import { ProsemirrorDefaultConfig } from '../../configs/default-config';
import { ProsemirrorViewboxConfig } from '../../configs/viewbox-config';
import { EditorView$, MentionsStream$, StateUpdated$ } from '../../consts';
import { OVERLAY_PROVIDERS, SCROLL_PROVIDER } from '../../providers';
import { ContextToolbarService } from '../context-toolbar/context-toolbar.service';
import { LinkComponentService } from '../link-input/link-component.service';
import { MentionsListService } from '../mentions/mentions-list.service';
import { ToolbarService } from '../toolbar/toolbar.service';
import { ProsemirrorConfig, ProsemirrorService } from './prosemirror.service';

const WYSIWYG_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @angular-eslint/no-forward-ref
  useExisting: forwardRef(() => WysiwgComponent),
  multi: true,
};

// prettier-ignore
export type KeyConfigMap<C extends ProsemirrorConfig> =
  C extends ProsemirrorConfig.Default ? ProsemirrorDefaultConfig :
  C extends ProsemirrorConfig.ViewboxNotes ? ProsemirrorViewboxConfig :
  never;

@Component({
  selector: 'rt-wysiwg',
  templateUrl: './wysiwg.component.html',
  styleUrls: ['./wysiwg.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ProsemirrorService,
    ContextToolbarService,
    StateUpdated$,
    ToolbarService,
    LinkComponentService,
    MentionsListService,
    EmojiPickerService,
    ProsemirrorDefaultConfig,
    ProsemirrorViewboxConfig,
    EditorView$,
    WYSIWYG_VALUE_ACCESSOR,
    OVERLAY_PROVIDERS,
    SCROLL_PROVIDER,
  ],
  host: {
    tabindex: '0',
    '(focusin)': 'onFocusIn($event)',
    '(focusout)': 'onFocusOut($event)',
    '[class.no-border]': '!withBorder',
  },
})
export class WysiwgComponent<C extends ProsemirrorConfig>
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  public editorView: EditorView;

  @Input() public config: C;
  @Input() public withBorder = true;
  @Input() public maxHeight: string;
  @Input() public toolbarVisible = true;
  @Input() public footerTmpl: TemplateRef<void>;

  @Input() public placeholder: string;
  @Input() public actionButtons: EditorButton[] = [];

  @Output() public focusChange = new EventEmitter<boolean>();
  @Output() public editorEvents = new EventEmitter<PluginsEvents<KeyConfigMap<C>>>();

  @ViewChild('editor', { static: true })
  public editorElement: ElementRef<HTMLElement>;

  private initialValue: EditorJSONState;
  private onChange: (val: any) => void;
  private editorSchema: ReturnType<ProsemirrorService['createEditor']>['schema'];
  private editorApi: ReturnType<ProsemirrorService['createEditor']>['publicApi'];
  private ngOnDestroy$ = new Subject();
  private hasFocus: boolean = false;

  constructor(
    private prosemirrorService: ProsemirrorService,
    private toolbarService: ToolbarService,
    private stateUpdated$: StateUpdated$,
    private elRef: ElementRef,
    private renderer: Renderer2,
    private cdRef: ChangeDetectorRef,
    private editorView$: EditorView$,
    @Optional()
    private mentionsProvider$: MentionsStream$,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if ('actionButtons' in changes) {
      this.toolbarService.setAdditionalButtons(this.actionButtons);
    }
  }

  public ngOnInit(): void {
    const { view, schema, publicApi, eventStream$ } = this.prosemirrorService.createEditor(
      this.editorElement.nativeElement,
      this.config,
      {
        mode: EditorMode.Wysiwyg,
        placeholder: this.placeholder,
        initialState: this.initialValue,
      },
    );

    eventStream$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((event: any) => this.editorEvents.next(event));
    this.editorView = view;
    this.editorView$.next(view);
    this.editorSchema = schema;
    this.editorApi = publicApi;

    this.stateUpdated$
      .pipe(
        startWith(view.state),
        distinctUntilChanged((s1, s2) => s1.doc.eq(s2.doc)),
        map(state => {
          const { selection, ...temp } = state.toJSON();
          const jsonState = <EditorJSONState>temp;

          return isEmptyDoc(jsonState) ? null : jsonState;
        }),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(state => {
        if (!this.onChange) return;

        this.onChange(state);
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public registerOnChange(fn: (v: any) => void): void {
    this.onChange = fn;
  }

  public onTouched: Function = () => void 0;

  public registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    const { nativeElement } = this.elRef;
    const className = 'disabled';

    if (isDisabled) {
      this.renderer.addClass(nativeElement, className);
    } else {
      this.renderer.removeClass(nativeElement, className);
    }
  }

  public writeValue(jsonState: EditorJSONState): void {
    if (!this.editorView) {
      this.initialValue = jsonState;
      return;
    }
    this.prosemirrorService.updateState(jsonState);
  }

  public onFocusIn(event: FocusEvent) {
    this.hasFocus = true;
    this.focusChange.next(this.hasFocus);
  }

  public onFocusOut(event: FocusEvent) {
    const wysiwygElement: HTMLElement = this.elRef.nativeElement;
    const target = <HTMLElement>event.relatedTarget;
    if (wysiwygElement.contains(target)) return;

    this.hasFocus = false;
    this.focusChange.next(this.hasFocus);
    this.onTouched();
  }
}
